import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ParagraphSM, TitleLG, TitleMD, TitleXS } from '@/atoms/Text'
import { EmbeddedStripeCheckoutSession } from '@/molecules/EmbeddedStripeCheckoutSession'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { getStringFromSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { CheckInputButton } from '../../components/CheckInputButton'
import { Page } from '../../components/Page'
import { PageSection } from '../../components/PageSection'
import { TorchApplicationStatus, useTorchApplication } from '../../components/TorchApplicationContext'
import { CREATE_TORCH_APPLICATION_STRIPE_CHECKOUT } from '../../queries'
import { TORCH_FULL_FILM_APPLICATION_FEE } from '../../utils/payment'
import { SubmissionSuccess } from './SubmissionSuccess'

enum ProtectionType {
  Basic = 'basic',
  Full = 'full',
}

export const FilmApplicationPayment = () => {
  const { track } = useSafeAnalytics()
  const { t } = useTranslate('guild-create')
  const { updateStatus, status, data, id, setPage } = useTorchApplication()
  const { query } = useSafeRouter()
  const [selection, setSelection] = useState<ProtectionType | null>(null)
  const [showPayment, setShowPayment] = useState(false)

  const [createSession, { data: checkoutSession, loading }] = useMutation(CREATE_TORCH_APPLICATION_STRIPE_CHECKOUT, {
    errorPolicy: 'all',
  })
  const hasPaid = !!data?.paymentDate || query.success || status === TorchApplicationStatus.PendingReview

  const clientSecret = checkoutSession?.createTorchApplicationStripeCheckout?.clientSecret

  const handleSubmission = () => {
    const source = new URLSearchParams(window.location.search).get('source') || undefined
    track('Guild Create Application Submitted', { source })
    if (selection === ProtectionType.Basic) {
      updateStatus(TorchApplicationStatus.PendingReview)
      return
    }
    const promoCode = getStringFromSessionStorage('torchSubmissionPromoCode')
    !clientSecret && createSession({ variables: { input: { id, promoCode } } })
    setShowPayment(true)
  }

  const options = [
    t(
      'angelFullFilmNotice2WeekExpire',
      'Angel will only make your film available to viewers for 1 week from the time they start watching it.',
    ),
    t(
      'angelWillPlaceWatermarkGuildUseOnly',
      'Angel will place a visible watermark on the film displaying "Guild Use Only".',
    ),
    t(
      'angelPlaceWatermarkTrack',
      'Angel will place an invisible watermark on the film so each stream can be tracked back to the original viewer.',
    ),
  ]

  const plans = [
    {
      name: ProtectionType.Basic,
      title: t('basicProtection', 'Basic Protection'),
      excluded: [2],
      price: '$0',
    },
    {
      name: ProtectionType.Full,
      title: t('advancedProtection', 'Advanced Protection'),
      excluded: [],
      price: TORCH_FULL_FILM_APPLICATION_FEE,
    },
  ]

  const onBack = useCallback(() => {
    if (showPayment) {
      setShowPayment(false)
      return
    }

    setPage(4)
  }, [setPage, showPayment])

  if (hasPaid) {
    return (
      <Page>
        <SubmissionSuccess />
      </Page>
    )
  }

  return (
    <Page onBack={onBack}>
      {showPayment ? (
        <div className="min-h-[560px] w-full max-w-[450px] lg:max-w-[1000px]">
          <EmbeddedStripeCheckoutSession clientSecret={clientSecret} loading={loading} />
        </div>
      ) : (
        <>
          <PageSection>
            <TitleMD weight="bold">{t('applicationFee', 'Application Fee')}</TitleMD>
            <ParagraphSM color="core-gray-600">
              {t(
                'pleaseSelectTheTypeOfProtection',
                'Please select the type of protection you would like for your film.',
              )}
            </ParagraphSM>
            <div className="mt-4 flex flex-col items-center gap-4">
              {plans.map((plan, index) => (
                <CheckInputButton
                  key={index}
                  name={plan.name}
                  selected={plan.name === selection}
                  onClick={() => setSelection(plan.name)}
                  className="w-full flex-col items-start gap-2 pl-10"
                  iconClassName={classNames('left-1.5 !block !fill-current text-core-gray-400')}
                >
                  <TitleXS weight="bold">{plan.title}</TitleXS>
                  <div className="flex flex-col items-start gap-2">
                    {options.map((option, index) => (
                      <div key={index} className="flex flex-row items-start gap-2">
                        {plan.excluded.includes(index) ? (
                          <CloseIcon color="core-gray-400" size={22} className="shrink-0" />
                        ) : (
                          <CheckIcon color="oxide" size={24} className="shrink-0" />
                        )}
                        <ParagraphSM
                          color={plan.excluded.includes(index) ? 'core-gray-400' : 'core-gray-800'}
                          className="text-left"
                        >
                          {option}
                        </ParagraphSM>
                      </div>
                    ))}
                  </div>
                  <div className="absolute right-2 top-2">
                    <TitleLG weight="bold" color="core-gray-950">
                      {plan.price}
                    </TitleLG>
                  </div>
                </CheckInputButton>
              ))}
            </div>
          </PageSection>

          <Button variant="oxide" disabled={!selection || loading} onClick={handleSubmission} className="w-72">
            {selection === ProtectionType.Basic
              ? t('submitApplication', 'Submit Application')
              : t('continueToPayment', 'Continue To Payment')}
          </Button>
        </>
      )}
    </Page>
  )
}
